import React, { useId, useState } from 'react'
import Style from './keyMetrics.module.css'

const WIDTH = 300
const HEIGHT = 120
const TOP_PAD = 8

const monotonePath = (points) => {
    if (points.length < 2) return ''
    const n = points.length
    const dx = []
    const slope = []
    for (let i = 0; i < n - 1; i++) {
        dx.push(points[i + 1].x - points[i].x)
        slope.push((points[i + 1].y - points[i].y) / dx[i])
    }
    const tangent = [slope[0]]
    for (let i = 1; i < n - 1; i++) {
        tangent.push(slope[i - 1] * slope[i] <= 0 ? 0 : (slope[i - 1] + slope[i]) / 2)
    }
    tangent.push(slope[n - 2])
    for (let i = 0; i < n - 1; i++) {
        if (slope[i] === 0) {
            tangent[i] = 0
            tangent[i + 1] = 0
            continue
        }
        const a = tangent[i] / slope[i]
        const b = tangent[i + 1] / slope[i]
        const h = a * a + b * b
        if (h > 9) {
            const t = 3 / Math.sqrt(h)
            tangent[i] = t * a * slope[i]
            tangent[i + 1] = t * b * slope[i]
        }
    }
    let d = `M${points[0].x},${points[0].y}`
    for (let i = 0; i < n - 1; i++) {
        const third = dx[i] / 3
        d += ` C${points[i].x + third},${points[i].y + tangent[i] * third} ${points[i + 1].x - third},${points[i + 1].y - tangent[i + 1] * third} ${points[i + 1].x},${points[i + 1].y}`
    }
    return d
}

const formatValue = (value, unit) => (unit === '%' ? `${Number(value.toFixed(2))}%` : value.toLocaleString())

const TrendChart = ({ labels, series, unit }) => {
    const [hovered, setHovered] = useState(null)
    const gradientId = `trend${useId().replace(/:/g, '')}`
    const count = labels.length
    const max = Math.max(1, ...series.flatMap((item) => item.values))
    const xAt = (index) => (count > 1 ? (index / (count - 1)) * WIDTH : WIDTH / 2)
    const yAt = (value) => TOP_PAD + (1 - value / max) * (HEIGHT - TOP_PAD)
    const toPercentX = (index) => (xAt(index) / WIDTH) * 100
    const toPercentY = (value) => (yAt(value) / HEIGHT) * 100

    const handleMove = (event) => {
        const rect = event.currentTarget.getBoundingClientRect()
        const ratio = (event.clientX - rect.left) / rect.width
        setHovered(Math.min(count - 1, Math.max(0, Math.round(ratio * (count - 1)))))
    }

    if (count === 0) return <div className={Style.empty}>No data for the last 6 months</div>

    return (
        <div className={Style.chart}>
            <div className={Style.plot} onMouseMove={handleMove} onMouseLeave={() => setHovered(null)}>
                <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} preserveAspectRatio="none" className={Style.svg} aria-hidden="true">
                    {[0, 0.5, 1].map((fraction) => (
                        <line
                            key={fraction}
                            x1="0"
                            x2={WIDTH}
                            y1={TOP_PAD + fraction * (HEIGHT - TOP_PAD)}
                            y2={TOP_PAD + fraction * (HEIGHT - TOP_PAD)}
                            className={Style.grid}
                            vectorEffect="non-scaling-stroke"
                        />
                    ))}
                    <defs>
                        {series.map((item, index) => (
                            <linearGradient key={item.name} id={`${gradientId}-${index}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={item.color} stopOpacity="0.1" />
                                <stop offset="100%" stopColor={item.color} stopOpacity="0" />
                            </linearGradient>
                        ))}
                    </defs>
                    {series.map((item, index) => {
                        const line = monotonePath(item.values.map((value, i) => ({ x: xAt(i), y: yAt(value) })))
                        return (
                            <g key={item.name}>
                                <path d={`${line} L${xAt(count - 1)},${HEIGHT} L${xAt(0)},${HEIGHT} Z`} fill={`url(#${gradientId}-${index})`} />
                                <path
                                    d={line}
                                    fill="none"
                                    stroke={item.color}
                                    strokeWidth="1.75"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    vectorEffect="non-scaling-stroke"
                                />
                            </g>
                        )
                    })}
                </svg>
                {hovered !== null ? (
                    <>
                        <span className={Style.guide} style={{ left: `${toPercentX(hovered)}%` }} />
                        {series.map((item) => (
                            <span
                                key={item.name}
                                className={Style.dot}
                                style={{ left: `${toPercentX(hovered)}%`, top: `${toPercentY(item.values[hovered])}%`, background: item.color }}
                            />
                        ))}
                        <div
                            className={[Style.tooltip, hovered > (count - 1) / 2 ? Style.tooltipLeft : ''].join(' ')}
                            style={{ left: `${toPercentX(hovered)}%` }}
                        >
                            <div className={Style.tooltipTitle}>{labels[hovered]}</div>
                            {series.map((item) => (
                                <div key={item.name} className={Style.tooltipRow}>
                                    <span className={Style.swatch} style={{ background: item.color }} />
                                    <span className={Style.tooltipName}>{item.name}</span>
                                    <span className={Style.tooltipValue}>{formatValue(item.values[hovered], unit)}</span>
                                </div>
                            ))}
                        </div>
                    </>
                ) : null}
            </div>
            <div className={Style.axis}>
                {labels.map((label, index) => (
                    <span key={`${label}-${index}`} style={{ left: `${toPercentX(index)}%` }}>{label}</span>
                ))}
            </div>
        </div>
    )
}

export default TrendChart
